import type { FormationPosition, TrainingDiagram } from '@ukinfo/shared';
import { useRef, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { clampPosition } from '../../lib/formations';
import { cn } from '../ui';
import { clientToField } from './diagramEdit';
import { formationToElements } from './formationToElements';
import { PITCH_HEIGHT, PitchDiagram, PitchSurface, SketchElement, pitchViewBox, useMarkerId } from './PitchDiagram';

/**
 * Formationen auf dem Feld.
 *
 * Gezeichnet wird mit den Bausteinen der Skizze (`PitchDiagram`, `SketchElement`): Spieler mit ihrem
 * Kuerzel, der Torwart gelb. `FormationPreview` ist nur Anzeige; `FormationEditorPitch` laesst die
 * Spieler per Zeiger (Maus, Finger, Stift) verschieben - ein Zeigercode fuer alles.
 */

/** Die Formation als Skizze des ganzen Feldes - Grundlage der Vorschau und des Bildes. */
export function formationDiagram(positions: readonly FormationPosition[]): TrainingDiagram {
  return { pitch: 'FULL', elements: formationToElements({ positions }, { team: 'A', mirrored: false, pitch: 'FULL' }) };
}

export function FormationPreview({ positions, title, className }: { positions: readonly FormationPosition[]; title?: string; className?: string }): JSX.Element {
  return <PitchDiagram diagram={formationDiagram(positions)} title={title} className={className} />;
}

/** Ein Zug unter dieser Strecke (Bildschirmpixel) gilt noch als Tippen: dann bewegt sich nichts. */
const MOVE_THRESHOLD = 4;
const SELECT_COLOR = '#38bdf8';
/** Radius der Trefferflaeche eines Spielers in Zeichnungseinheiten - groesser als der Spieler selbst (Radius 3,4). */
const HIT_RADIUS = 6;

export function FormationEditorPitch({
  positions,
  selected,
  onSelect,
  onChange,
  disabled,
  className,
}: {
  positions: readonly FormationPosition[];
  selected: number | null;
  onSelect: (index: number | null) => void;
  onChange: (positions: FormationPosition[]) => void;
  disabled?: boolean;
  className?: string;
}): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ pointerId: number; index: number; screenX: number; screenY: number; moved: boolean } | null>(null);
  const markerId = useMarkerId();
  const height = PITCH_HEIGHT.FULL;
  const elements = formationToElements({ positions }, { team: 'A', mirrored: false, pitch: 'FULL' });

  const moveTo = (index: number, x: number, y: number): void => {
    const nx = clampPosition(x);
    const ny = clampPosition(y);
    const current = positions[index];
    if (!current || (current.x === nx && current.y === ny)) return;
    onChange(positions.map((position, at) => (at === index ? { ...position, x: nx, y: ny } : position)));
  };

  const fieldPoint = (event: ReactPointerEvent<SVGSVGElement>): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    return clientToField(event.clientX, event.clientY, svg.getBoundingClientRect(), 'FULL');
  };

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>): void => {
    if (disabled) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const hit = (event.target as Element).closest('[data-index]');
    if (!hit) {
      onSelect(null);
      return;
    }
    const index = Number(hit.getAttribute('data-index'));
    onSelect(index);
    drag.current = { pointerId: event.pointerId, index, screenX: event.clientX, screenY: event.clientY, moved: false };
    try {
      svgRef.current?.setPointerCapture(event.pointerId);
    } catch {
      // Ohne Zeigererfassung laufen die Ereignisse weiter, solange der Zeiger ueber dem Feld bleibt.
    }
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>): void => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (!current.moved) {
      if (Math.hypot(event.clientX - current.screenX, event.clientY - current.screenY) < MOVE_THRESHOLD) return;
      current.moved = true;
    }
    const point = fieldPoint(event);
    if (point) moveTo(current.index, point.x, point.y);
  };

  const end = (event: ReactPointerEvent<SVGSVGElement>): void => {
    if (drag.current?.pointerId === event.pointerId) drag.current = null;
  };

  /** Pfeiltasten schieben den markierten Spieler um 1, mit Umschalttaste um 5. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled || selected === null) return;
    const step = event.shiftKey ? 5 : 1;
    const delta: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const change = delta[event.key];
    const current = positions[selected];
    if (!change || !current) return;
    event.preventDefault();
    moveTo(selected, current.x + change[0], current.y + change[1]);
  };

  const mark = selected !== null ? positions[selected] : undefined;

  return (
    <div
      tabIndex={disabled ? -1 : 0}
      role="group"
      aria-label="Spielfeld mit der Formation – Spieler ziehen oder markieren und mit den Pfeiltasten verschieben"
      onKeyDown={onKeyDown}
      className={cn('mx-auto w-full max-w-xs rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 sm:max-w-sm', className)}
    >
      <svg
        ref={svgRef}
        role="img"
        aria-label={`Formation mit ${positions.length} Spielern`}
        viewBox={pitchViewBox('FULL')}
        data-testid="formation-canvas"
        data-pitch="FULL"
        className="block h-auto w-full select-none"
        // Ziehen auf dem Feld darf die Seite nicht scrollen: so liegt der Finger am Spieler.
        style={{ touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={end}
        onPointerCancel={end}
        onContextMenu={(event) => event.preventDefault()}
      >
        <PitchSurface pitch="FULL" markerId={markerId} />
        {elements.map((element, index) => (
          <g key={index} data-index={index} data-label={element.type === 'player' ? element.label : undefined} style={{ cursor: disabled ? 'default' : 'grab' }}>
            <SketchElement element={element} markerId={markerId} height={height} />
            {element.type === 'player' && <circle cx={element.x} cy={(element.y / 100) * height} r={HIT_RADIUS} fill="transparent" />}
          </g>
        ))}
        {mark && (
          <circle
            data-testid="formation-selected"
            cx={mark.x}
            cy={(mark.y / 100) * height}
            r={5}
            fill="none"
            stroke={SELECT_COLOR}
            strokeWidth={0.9}
            pointerEvents="none"
          />
        )}
      </svg>
    </div>
  );
}
